import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity, BrainCircuit, FlaskConical, LockKeyhole } from "lucide-react";
import { APP_KIND } from "@config";
import { getV2AccessToken, loginV2 } from "../auth/v2Auth";
import DigitalTwinIllustration from "../components/DigitalTwinIllustration";
import LanguageSwitcher from "../components/LanguageSwitcher";
import V2Brand from "../components/V2Brand";

const FEATURE_ICONS = [Activity, BrainCircuit, FlaskConical];

const INPUT =
  "h-[39px] w-full rounded border border-[rgba(98,94,166,0.75)] bg-[rgba(17,12,61,0.7)] px-3.5 text-[11px] text-v2-foreground outline-none focus:border-v2-cyan focus:shadow-v2-focus data-[invalid=true]:border-v2-bad";

function EyeIcon({ visible }) {
  return visible ? (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        d="M3 3l18 18M10.6 10.7a2 2 0 0 0 2.7 2.7M9.9 5.2A10.8 10.8 0 0 1 12 5c5.3 0 8.7 4.2 9.8 6.1a1.7 1.7 0 0 1 0 .9 15.5 15.5 0 0 1-3.2 3.8M6.2 6.3A15.2 15.2 0 0 0 2.2 11a1.8 1.8 0 0 0 0 2C3.4 15.1 6.8 19 12 19c1.1 0 2.1-.2 3-.5"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        d="M2.2 12S5.6 5 12 5s9.8 7 9.8 7-3.4 7-9.8 7-9.8-7-9.8-7Z"
      />
      <circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
    </svg>
  );
}

function V2LoginPage({ setToken, setClubId, setIsAuthenticated }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const language = (i18n.language || "fr").slice(0, 2);
  const features = t(`v2.login.features_${APP_KIND}`, { returnObjects: true });
  const featureList = Array.isArray(features) ? features : [];

  const validate = () => {
    const errors = {};
    if (!login.trim()) errors.login = t("v2.login.required");
    if (!password) errors.password = t("v2.login.required");
    else if (password.length < 6) errors.password = t("v2.login.short_password");
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFeedback(null);
    if (!validate()) return;

    setSubmitting(true);
    try {
      const data = await loginV2({ login: login.trim(), password, remember });
      const accessToken = data.access_token || data.token;

      if (!accessToken) {
        setFeedback({ type: "error", message: t("v2.login.network") });
        return;
      }

      setToken?.(accessToken);
      setClubId?.(data.club_id);
      setIsAuthenticated?.(true);
      navigate("/v2/overview", { replace: true });
    } catch (error) {
      setFeedback({
        type: "error",
        message: error?.status === 401 ? t("v2.login.invalid_credentials") : t("v2.login.network"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const clearFieldError = (field) => {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
    setFeedback(null);
  };

  if (getV2AccessToken()) {
    return <Navigate to="/v2/overview" replace />;
  }

  return (
    <div
      className="v2-login v2-root min-w-[1000px] bg-v2-background font-v2 text-[14px] leading-[1.55] text-v2-foreground"
      data-app={APP_KIND}
      lang={language}
    >
      <div className="absolute right-3 top-3 z-10">
        <LanguageSwitcher size="login" />
      </div>

      <section className="v2-login-art" aria-label={t(`v2.login.art_text_${APP_KIND}`)}>
        <div className="v2-art-network" aria-hidden="true" />
        <DigitalTwinIllustration appKind={APP_KIND} />
        <div className="v2-login-copy">
          <V2Brand appKind={APP_KIND} />
          <p className="mt-[22px] max-w-[290px] text-[14px] leading-[1.6] text-[rgba(243,242,255,0.68)]">
            {t(`v2.login.art_text_${APP_KIND}`)}
          </p>
          <ul className="mt-6 grid list-none gap-[11px] p-0 text-[13px] font-semibold text-[rgba(243,242,255,0.88)]">
            {featureList.map((feature, index) => {
              const FeatureIcon = FEATURE_ICONS[index] || Activity;
              return (
                <li key={feature} className="flex items-center gap-2">
                  <span
                    className="grid h-[22px] w-[22px] place-items-center rounded-[5px] border border-[rgba(52,203,255,0.55)] text-[14px] text-v2-cyan"
                    aria-hidden="true"
                  >
                    <FeatureIcon size={13} strokeWidth={1.8} />
                  </span>
                  {feature}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <section className="relative flex min-h-screen items-center justify-center px-[8%] py-10">
        <div className="w-[min(100%,360px)]">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.13em] text-v2-cyan">
            {t(`v2.login.eyebrow_${APP_KIND}`)}
          </p>
          <h2 className="v2-display mb-[7px] text-[44px] font-bold uppercase leading-none tracking-[0.01em] text-[#f7f7ff]">
            {t("v2.login.sign_in")}
          </h2>
          <p className="mb-7 text-[11px] text-[rgba(243,242,255,0.65)]">{t("v2.login.subtitle")}</p>

          {feedback && (
            <div
              className={`mb-5 rounded border p-3 text-v2-foreground ${
                feedback.type === "info"
                  ? "border-[rgba(52,203,255,0.38)] bg-v2-info-soft"
                  : "border-[rgba(255,77,94,0.42)] bg-v2-bad-soft"
              }`}
              role="alert"
              aria-live="assertive"
            >
              <p className="m-0 font-bold">{feedback.message}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-[17px]">
              <label htmlFor="v2-login" className="mb-1.5 block text-[10px] font-semibold text-[#f7f7ff]">
                {t("v2.login.login")}
              </label>
              <div className="relative">
                <input
                  id="v2-login"
                  className={INPUT}
                  type="text"
                  value={login}
                  autoComplete="username"
                  data-invalid={Boolean(fieldErrors.login)}
                  aria-invalid={Boolean(fieldErrors.login)}
                  aria-describedby={fieldErrors.login ? "v2-login-error" : undefined}
                  onChange={(event) => {
                    setLogin(event.target.value);
                    clearFieldError("login");
                  }}
                />
              </div>
              {fieldErrors.login && (
                <p className="mt-[7px] text-[12px] text-v2-bad" id="v2-login-error">{fieldErrors.login}</p>
              )}
            </div>

            <div className="mb-[17px]">
              <label htmlFor="v2-password" className="mb-1.5 block text-[10px] font-semibold text-[#f7f7ff]">
                {t("v2.login.password")}
              </label>
              <div className="relative">
                <input
                  id="v2-password"
                  className={`${INPUT} pr-12`}
                  type={passwordVisible ? "text" : "password"}
                  value={password}
                  autoComplete="current-password"
                  data-invalid={Boolean(fieldErrors.password)}
                  data-password-visible={passwordVisible}
                  aria-invalid={Boolean(fieldErrors.password)}
                  aria-describedby={fieldErrors.password ? "v2-password-error" : undefined}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    clearFieldError("password");
                  }}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded border-0 bg-transparent text-v2-muted hover:bg-v2-accent hover:text-v2-foreground"
                  aria-label={passwordVisible ? t("v2.login.hide_password") : t("v2.login.show_password")}
                  aria-pressed={passwordVisible}
                  onClick={() => setPasswordVisible((visible) => !visible)}
                >
                  <EyeIcon visible={passwordVisible} />
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-[7px] text-[12px] text-v2-bad" id="v2-password-error">{fieldErrors.password}</p>
              )}
            </div>

            <div className="mb-[22px] mt-[3px] flex items-center justify-between gap-4">
              <label className="inline-flex items-center gap-2 text-[9px] text-v2-muted">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-v2-brand"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                {t("v2.login.remember")}
              </label>
              <button
                type="button"
                className="border-0 bg-transparent p-0 text-[9px] text-v2-cyan hover:text-white hover:underline"
                onClick={() => setFeedback({ type: "info", message: t("v2.login.forgot_info") })}
              >
                {t("v2.login.forgot")}
              </button>
            </div>

            <button
              className="min-h-[39px] w-full rounded border border-v2-brand bg-v2-brand text-[11px] font-bold text-white transition-colors duration-150 hover:border-v2-brand-hover hover:bg-v2-brand-hover active:border-v2-brand-active active:bg-v2-brand-active disabled:cursor-not-allowed disabled:opacity-[0.58]"
              type="submit"
              disabled={submitting}
            >
              {submitting ? t("v2.login.busy") : t("v2.login.submit")}
              <span aria-hidden="true"> →</span>
            </button>
          </form>

          <p className="mt-[23px] flex max-w-[280px] items-start gap-1.5 text-[9px] leading-[1.5] text-[rgba(171,167,217,0.56)]">
            <LockKeyhole size={12} strokeWidth={1.7} aria-hidden="true" />
            <span>{t("v2.login.disclaimer")}</span>
          </p>
          <p className="absolute bottom-3 left-[18px] m-0 text-[9px] text-[rgba(171,167,217,0.48)]">
            {t("v2.login.version")}
          </p>
        </div>
      </section>
    </div>
  );
}

export default V2LoginPage;
